import React, { FunctionComponent, useState } from 'react'
import styles from './ObjectCloseDialog.module.sass'
import { Dialog } from '../../../etc/Dialog/Dialog'
import { ObjectCloseData, ObjectStatus, ObjectUpdateRequest, ProjectWorkObject } from '../../../../responses/objects'
import { useRootSelector } from '../../../../utils/hooks'
import { objectTasksSelector, pipeSelectors, tasksSelector } from '../../../../utils/selectors'
import { Task } from '../../../../responses/tasks'
import OkCancel from '../../../controls/OkCancel/OkCancel'
import { isDefined, pick } from '../../../../utils/utils'
import ObjectDesignPicker, { isObjectIncomplete } from '../../../etc/ObjectDesignPicker/ObjectDesignPicker'
import { TenderStatus } from '../../pickers/TenderPicker/TenderPicker'
import ObjectTitle from '../../../etc/ObjectTitle/ObjectTitle'
import { objectName } from '../../../../utils/lenses'
import { Lens } from 'monocle-ts'
import { pipe } from 'fp-ts/es6/pipeable'
import { isNonEmpty } from 'fp-ts/es6/Array'
import { map } from 'fp-ts/es6/NonEmptyArray'
import { RequestStatus } from '../../../../responses/response'
import { ID } from '../../../../types/UtilityTypes'
import QuillEditor from '../../../etc/Editor/QuillEditor'
import { CommentData } from '../../../../responses/comments'

interface Props {
    object: ProjectWorkObject,
    onClose: (data: ObjectCloseData) => void,
    onOpen: (data: { objectId: ID, changes: ObjectUpdateRequest }) => void,
    onCancel: () => void,
}

const workObjectName = Lens.fromPath<ProjectWorkObject>()(['work_object_type', 'work_object', 'name'])
export const constructCloseData = (object: ProjectWorkObject, tasks: Task[] = [], text?: string): ObjectCloseData => {
    const comment: CommentData | undefined = isDefined(text)? {text}: undefined
    return isNonEmpty(tasks)?
        {
            id: object.id,
            production_status: object.production_status,
            tasks: map((task: Task) => ({
                id: task.id,
                tender_id: task.tender_id,
                tendered_user: task.tendered_user?.id
            }))(tasks),
            comment,
        }:
        {
            ...pick(object, 'id', 'design_status', 'production_status'),
            tasks: [],
            comment,
        }
}

const ObjectCloseDialog: FunctionComponent<Props> = props => {
    const stateTasks = useRootSelector(pipeSelectors(props.object.id, objectTasksSelector, tasksSelector))
    const fetching = useRootSelector(state => state.data.object.closeStatus === RequestStatus.FETCHING)

    const [comment, setComment] = useState('')
    const [object, setObject] = useState(props.object)
    const [tasks, setTasks] = useState<Task[]>(stateTasks.filter(task => task.tender_id !== TenderStatus.CANCELED))

    const open = props.object.status_id === ObjectStatus.OPENED
    const text = open ? 'Закрыть' : 'Открыть'
    const incomplete = open && isObjectIncomplete(object, tasks)

    return <Dialog
        className={styles.dialog}
        onClose={props.onCancel}
        headerClassName={styles.header}
        zIndex={200}
        header={<ObjectTitle
            className={styles.title}
            object={pipe(props.object, objectName.set(''), workObjectName.set(`${text} объект`))}
            arrow={'none'}
        >
            <div className={styles.warning}>{incomplete && 'Проставьте статусы и исполнителей'}</div>
        </ObjectTitle>}
        actions={<OkCancel
            text={text}
            disabled={incomplete || fetching}
            onOk={() => open?
                props.onClose(constructCloseData(object, tasks)):
                props.onOpen({ objectId: props.object.id, changes: { status_id: ObjectStatus.OPENED } })
            }
            onCancel={props.onCancel}
        />}
    >
        {
            open && <ObjectDesignPicker
                withoutHeader
                object={object}
                tasks={tasks}
                objectChange={setObject}
                tasksChange={setTasks}
            />
        }
        <QuillEditor className={styles.editor} value={comment} onChange={setComment}/>
    </Dialog>
}

export default ObjectCloseDialog